import React, { useCallback, useEffect, useState } from 'react';

import { Check as CheckIcon } from '@styled-icons/boxicons-regular/Check';
import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Input,
  HStack,
  Select,
  SimpleGrid,
  Text,
  Icon,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';
import PaginationWrapper from 'components/Pagination';

import useDebouncedState from 'hooks/useDebouncedState';
import useThrottledState from 'hooks/useThrottledState';

import { CompositionGroup } from 'types/composition';
import { Pagination, ServicePagination } from 'types/pagination';

import CompositionGroupsTable from './CompositionGroupsTable';
import { ROUTE_BY_RESOURCE_TYPE } from './shared';
import { SelectedCompositionGroup } from './types';

type ModalCompositionGroupsLookup = Omit<ModalRootProps, 'onConfirm'> & {
  data: {
    resourceType: 'service' | 'input';
    multiSelect?: boolean;
  };
  onConfirm?: (
    selection: SelectedCompositionGroup | SelectedCompositionGroup[],
  ) => void;
};

const ModalCompositionGroupsLookup: React.FC<ModalCompositionGroupsLookup> = ({
  data: { resourceType, multiSelect = false } = { resourceType: 'service' },
  onConfirm,
  handleClose,
  ...restProps
}) => {
  const [groups, setGroups] = useState<CompositionGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<
    SelectedCompositionGroup[]
  >([]);

  const [codeFilter, setCodeFilter] = useDebouncedState('');
  const [descriptionFilter, setDescriptionFilter] = useDebouncedState('');
  const [keywordFilter, setKeywordFilter] = useDebouncedState('');

  const normalizeKeyword = useCallback((value: string): string => {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }, []);

  const [pagination, setPagination] = useThrottledState<Pagination>(
    (() => {
      return {
        per_page: 10,
        page: 1,
      };
    })(),
    1000,
  );

  const [servicePagination, setServicePagination] = useState<ServicePagination>(
    { last_page: 1 },
  );

  const getData = useCallback(async () => {
    setLoading(true);
    setGroups([]);

    try {
      const trimmedCode = codeFilter.trim();
      const trimmedDescription = descriptionFilter.trim();
      const trimmedKeyword = keywordFilter.trim();
      const normalizedKeyword = normalizeKeyword(trimmedKeyword);

      const response = await api.get(ROUTE_BY_RESOURCE_TYPE[resourceType], {
        params: {
          'filter[level]': 1,
          'filter[status]': 'ACTIVE',
          page: pagination.page,
          per_page: pagination.per_page,
          ...(resourceType === 'service'
            ? {
                ...(trimmedCode && {
                  'filter[workItemId][0]': trimmedCode,
                }),
                ...(trimmedDescription && {
                  'filter[description]': trimmedDescription,
                }),
              }
            : {
                ...(normalizedKeyword && {
                  'filter[keyword]': normalizedKeyword,
                }),
              }),
        },
      });

      const componentsData = response.data;
      const newPagination = {
        last_page: componentsData.meta?.last_page || 1,
      };

      setGroups(componentsData.data);
      setServicePagination(newPagination);
    } catch (err) {
      setGroups([]);
      setServicePagination({ last_page: 1 });

      toast({
        status: 'error',
        description:
          err.response?.data?.message ||
          'Houve um erro ao buscar os grupos de serviço',
      });
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    codeFilter,
    descriptionFilter,
    keywordFilter,
    normalizeKeyword,
    resourceType,
  ]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (pagination.page > servicePagination?.last_page) {
      setPagination((oldPagination) => {
        if (oldPagination.page > 1) {
          return {
            ...oldPagination,
            page: 1,
          };
        }

        return oldPagination;
      });
    }
  }, [pagination.page, servicePagination, setPagination]);

  useEffect(() => {
    setPagination((old) => {
      if (old.page !== 1) {
        return { ...old, page: 1 };
      }

      return old;
    });
  }, [
    codeFilter,
    descriptionFilter,
    keywordFilter,
    resourceType,
    setPagination,
  ]);

  useEffect(() => {
    if (multiSelect) {
      setSelectedGroups([]);
    }
  }, [multiSelect, resourceType]);

  const buildSelectionKey = useCallback(
    (
      parentGroup: CompositionGroup,
      selectedGroup: CompositionGroup,
    ): string => {
      return `${parentGroup.key}-${selectedGroup.key}`;
    },
    [],
  );

  const handleRemoveSelectedGroup = useCallback(
    (parentGroup: CompositionGroup, selectedGroup: CompositionGroup) => {
      setSelectedGroups((prevSelected) =>
        prevSelected.filter(
          (item) =>
            buildSelectionKey(item.parentGroup, item.selectedGroup) !==
            buildSelectionKey(parentGroup, selectedGroup),
        ),
      );
    },
    [buildSelectionKey],
  );

  const handleSubmitSelectedGroups = useCallback(() => {
    if (!selectedGroups.length) return;

    if (onConfirm) {
      onConfirm(selectedGroups);
    }

    handleClose();
  }, [handleClose, onConfirm, selectedGroups]);

  const isGroupSelected = useCallback(
    (parentGroup: CompositionGroup, selectedGroup: CompositionGroup) => {
      return selectedGroups.some(
        (item) =>
          buildSelectionKey(item.parentGroup, item.selectedGroup) ===
          buildSelectionKey(parentGroup, selectedGroup),
      );
    },
    [buildSelectionKey, selectedGroups],
  );

  const handleSelectGroup = useCallback(
    (parentGroup: CompositionGroup, selectedGroup: CompositionGroup) => {
      if (multiSelect) {
        setSelectedGroups((prevSelected) => {
          const selectionKey = buildSelectionKey(parentGroup, selectedGroup);
          const alreadySelected = prevSelected.some(
            (item) =>
              buildSelectionKey(item.parentGroup, item.selectedGroup) ===
              selectionKey,
          );

          if (alreadySelected) {
            return prevSelected;
          }

          return [...prevSelected, { parentGroup, selectedGroup }];
        });

        return;
      }

      if (onConfirm) {
        onConfirm({ parentGroup, selectedGroup });

        handleClose();
      }
    },
    [buildSelectionKey, handleClose, multiSelect, onConfirm],
  );

  return (
    <Modal {...restProps} size="6xl" scrollBehavior="inside" closeOnEsc={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {resourceType === 'service'
            ? 'Consultar Grupos de Serviço'
            : 'Consultar Grupos de Insumo'}
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          {multiSelect && (
            <Flex
              mt={2}
              mb={4}
              direction={{ base: 'column', md: 'row' }}
              align={{ base: 'flex-start', md: 'center' }}
              justify="space-between"
              gap={3}
            >
              <Box>
                <Text fontWeight="medium" color="gray.600">
                  {selectedGroups.length === 0
                    ? 'Nenhum grupo selecionado.'
                    : `${selectedGroups.length} grupo${
                        selectedGroups.length > 1 ? 's' : ''
                      } selecionado${selectedGroups.length > 1 ? 's' : ''}.`}
                </Text>
              </Box>

              <Button
                alignSelf={{ base: 'stretch', md: 'flex-end' }}
                colorScheme="green"
                leftIcon={<Icon as={CheckIcon} />}
                isDisabled={selectedGroups.length === 0}
                onClick={handleSubmitSelectedGroups}
              >
                Confirmar seleção
              </Button>
            </Flex>
          )}

          <SimpleGrid columns={{ base: 1, md: 12 }} spacing={3} mt={4} mb={8}>
            {resourceType === 'service' ? (
              <>
                <FormControl gridColumn={{ base: 'span 12', md: 'span 3' }}>
                  <FormLabel>Código</FormLabel>
                  <Input
                    defaultValue={codeFilter}
                    onChange={(e) => setCodeFilter(e.target.value)}
                    placeholder="Buscar código"
                  />
                </FormControl>

                <FormControl gridColumn={{ base: 'span 12', md: 'span 6' }}>
                  <FormLabel>Descrição</FormLabel>
                  <Input
                    defaultValue={descriptionFilter}
                    onChange={(e) => setDescriptionFilter(e.target.value)}
                    placeholder="Buscar descrição"
                  />
                </FormControl>
              </>
            ) : (
              <FormControl gridColumn={{ base: 'span 12', md: 'span 9' }}>
                <FormLabel>Pesquisar por</FormLabel>
                <Input
                  defaultValue={keywordFilter}
                  onChange={(e) => setKeywordFilter(e.target.value)}
                  placeholder="Pesquisar por código ou descrição"
                />
              </FormControl>
            )}

            <Flex
              gridColumn={{ base: 'span 12', md: 'span 3' }}
              alignSelf="end"
              justifyContent="flex-end"
            >
              <HStack mb={{ base: 0, md: 1 }}>
                <Text fontSize="smaller">Por página:</Text>
                <Select
                  width="auto"
                  value={pagination.per_page}
                  onChange={(e) => {
                    setPagination({
                      ...pagination,
                      per_page: Number(e.target.value),
                    });
                  }}
                >
                  {[5, 10, 20, 50, 100].map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Select>
              </HStack>
            </Flex>
          </SimpleGrid>

          <CompositionGroupsTable
            data={groups}
            loading={loading}
            resourceType={resourceType}
            onSelectGroup={(parent, selected) => {
              handleSelectGroup(parent, selected);
            }}
            onRemoveGroup={(parent, selected) =>
              handleRemoveSelectedGroup(parent, selected)
            }
            isGroupSelected={(parent, selected) =>
              isGroupSelected(parent, selected)
            }
            multiSelect={multiSelect}
          />

          <PaginationWrapper
            lastPage={servicePagination.last_page}
            onPaginate={(selectedPage) => {
              setPagination({ ...pagination, page: selectedPage });
            }}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ModalCompositionGroupsLookup;
